import React, { useState, useMemo } from "react";
import { LuUsers, LuCalendar, LuBarChart3, LuRefreshCw } from "react-icons/lu";

import { EnhancedFilterState } from "../../pages/enhanced-dashboard";
import { Select } from "src/components/Form/select";
import Button from "src/components/Button/Button";
import { FaFilter, FaChartBar, FaEye, FaEyeSlash, FaExclamationTriangle } from "react-icons/fa";
import { languageTuples } from "src/utils/util";
import { UserLangCode } from "src/utils/util.types";
import { getTimezoneOptionsForUserGrowth } from "src/utils/country.util";
import { 
  LineChart, 
  Line, 
  AreaChart, 
  Area, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer, 
  Legend 
} from "recharts";
import { useTrendData } from "../../hooks/useTrendData";
import { TrendChartLoading } from "./trend-chart-loading";
import { TrendDataFilters } from "../../services/trend-data.service";

type Props = {
  filters: EnhancedFilterState;
};

const chartTypeOptions = [
  { value: "line", label: "Line Chart" },
  { value: "area", label: "Area Chart" },
  { value: "bar", label: "Bar Chart" },
];

const timeGranularityOptions = [
  { value: "daily", label: "Daily (Last 30 Days)" },
  { value: "weekly", label: "Weekly (Last 12 Weeks)" },
  { value: "custom", label: "Custom Date Range" },
];

export const EnhancedUserGrowthSection: React.FC<Props> = ({ filters }) => {
  const [sectionFilters, setSectionFilters] = useState({
    chartType: "line",
    timeGranularity: "daily",
    role: "ALL" as "COACH" | "LEARNER" | "ALL" | "DUAL",
    language: undefined as UserLangCode | undefined,
    timeZone: "ALL" as string | undefined, // Default to "ALL" instead of undefined
    showRoles: true,
    showFilters: false,
    startDate: undefined as Date | undefined,
    endDate: new Date() as Date | undefined, // Default to current day
  });

  // Convert section filters to API filters
  const apiFilters: TrendDataFilters = useMemo(() => ({
    timeGranularity: sectionFilters.timeGranularity as "daily" | "weekly" | "custom",
    metric: "users", // Fixed to users for this section
    role: sectionFilters.role,
    language: sectionFilters.language,
    timeZone: sectionFilters.timeZone,
    startDate: sectionFilters.startDate,
    endDate: sectionFilters.endDate,
  }), [sectionFilters.timeGranularity, sectionFilters.role, sectionFilters.language, sectionFilters.timeZone, sectionFilters.startDate, sectionFilters.endDate]);

  // Fetch real trend data using the custom hook
  const { data: trendData, loading, error, refetch, isInitialLoading } = useTrendData(apiFilters);

  const handleSectionFilterChange = (key: string, value: string | boolean) => {
    setSectionFilters(prev => ({ ...prev, [key]: value }));
  };

  const toggleFilters = () => {
    setSectionFilters(prev => ({ ...prev, showFilters: !prev.showFilters }));
  };

  const toggleRoles = () => {
    setSectionFilters(prev => ({ ...prev, showRoles: !prev.showRoles }));
  };

  const renderChart = () => {
    if (!trendData?.data) return null;
    

    
    // Filter data based on selected role
    let filteredData = trendData.data;
    if (sectionFilters.role !== "ALL") {
      filteredData = trendData.data.map(point => {
        // Get the value for the selected role
        let selectedRoleValue = 0;
        if (sectionFilters.role === "LEARNER") {
          selectedRoleValue = Number(point.learners) || 0;
        } else if (sectionFilters.role === "COACH") {
          selectedRoleValue = Number(point.coaches) || 0;
        } else if (sectionFilters.role === "DUAL") {
          selectedRoleValue = Number(point.dual) || 0;
        }
        
        return {
          ...point,
          total: selectedRoleValue,
          // When filtering by role, only show that role's data
          learners: sectionFilters.role === "LEARNER" ? selectedRoleValue : 0,
          coaches: sectionFilters.role === "COACH" ? selectedRoleValue : 0,
          dual: sectionFilters.role === "DUAL" ? selectedRoleValue : 0,
        };
      });
    }
    
    const commonProps = {
      data: filteredData,
      margin: { top: 5, right: 30, left: 20, bottom: 5 },
    };

    const renderTooltip = (props: any) => {
      const { active, payload, label } = props;
      if (active && payload && payload.length) {
        return (
          <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
            <p className="font-medium text-gray-900">{label}</p>
            {payload.map((entry: any, index: number) => (
              <p key={index} style={{ color: entry.color }} className="text-sm">
                {entry.name}: {entry.value}
              </p>
            ))}
          </div>
        );
      }
      return null;
    };

    switch (sectionFilters.chartType) {
      case "line":
        return (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="date"
                stroke="#6b7280"
                fontSize={12}
              />
              <YAxis stroke="#6b7280" fontSize={12} />
              <Tooltip content={renderTooltip} />
              <Legend />
              {sectionFilters.showRoles && (
                <>
                  <Line 
                    type="monotone" 
                    dataKey="learners" 
                    stroke="#00bfff" 
                    strokeWidth={2}
                    dot={{ fill: "#00bfff", strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="coaches" 
                    stroke="#ff4500" 
                    strokeWidth={2}
                    dot={{ fill: "#ff4500", strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="dual" 
                    stroke="#32cd32" 
                    strokeWidth={2}
                    dot={{ fill: "#32cd32", strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </>
              )}
              <Line 
                type="monotone" 
                dataKey="total" 
                stroke="#6b7280" 
                strokeWidth={3}
                dot={{ fill: "#6b7280", strokeWidth: 2, r: 5 }}
                activeDot={{ r: 7 }}
              />
            </LineChart>
          </ResponsiveContainer>
        );

      case "area":
        return (
          <ResponsiveContainer width="100%" height={400}>
            <AreaChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="date"
                stroke="#6b7280"
                fontSize={12}
              />
              <YAxis stroke="#6b7280" fontSize={12} />
              <Tooltip content={renderTooltip} />
              <Legend />
              {sectionFilters.showRoles && (
                <>
                  <Area 
                    type="monotone" 
                    dataKey="learners" 
                    stroke="#00bfff" 
                    fill="#00bfff" 
                    fillOpacity={0.4}
                    strokeWidth={2}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="coaches" 
                    stroke="#ff4500" 
                    fill="#ff4500" 
                    fillOpacity={0.4}
                    strokeWidth={2}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="dual" 
                    stroke="#32cd32" 
                    fill="#32cd32" 
                    fillOpacity={0.4}
                    strokeWidth={2}
                  />
                </>
              )}
              <Area 
                type="monotone" 
                dataKey="total" 
                stroke="#6b7280" 
                fill="#6b7280" 
                fillOpacity={0.3}
                strokeWidth={3}
              />
            </AreaChart>
          </ResponsiveContainer>
        );

      case "bar":
        return (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="date"
                stroke="#6b7280"
                fontSize={12}
              />
              <YAxis stroke="#6b7280" fontSize={12} />
              <Tooltip content={renderTooltip} />
              <Legend />
              {sectionFilters.showRoles && (
                <>
                  <Bar dataKey="learners" fill="#00bfff" />
                  <Bar dataKey="coaches" fill="#ff4500" />
                  <Bar dataKey="dual" fill="#32cd32" />
                </>
              )}
              <Bar dataKey="total" fill="#6b7280" />
            </BarChart>
          </ResponsiveContainer>
        );

      default:
        return null;
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center gap-2 mb-6">
        <LuUsers className="w-5 h-5 text-gray-700" />
        <h2 className="text-lg font-semibold text-gray-900">User Growth Trends</h2>
      </div>
        
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Button
              type="button"
              onClick={toggleRoles}
              className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 flex items-center gap-2 shadow-sm hover:shadow-md ${
                sectionFilters.showRoles
                  ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700"
                  : "bg-gradient-to-r from-gray-500 to-gray-600 text-white hover:from-gray-600 hover:to-gray-700"
              }`}
            >
              {sectionFilters.showRoles ? <FaEye className="text-sm" /> : <FaEyeSlash className="text-sm" />}
              {sectionFilters.showRoles ? "Hide" : "Show"} Individual Roles
            </Button>
            
            <Button
              type="button"
              onClick={toggleFilters}
              className="bg-gradient-to-r from-slate-500 to-slate-600 hover:from-slate-600 hover:to-slate-700 text-white px-4 py-2.5 rounded-xl flex items-center gap-2.5 text-sm font-medium shadow-lg shadow-slate-500/25 hover:shadow-slate-500/40 transition-all duration-200 transform hover:scale-105"
            >
              <FaFilter className="text-sm" />
              {sectionFilters.showFilters ? "Hide" : "Show"} Filters
            </Button>
          </div>
        </div>

      {/* Section-Specific Filters */}
      {sectionFilters.showFilters && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
            <FaChartBar className="text-gray-500" />
            Chart Configuration
          </h3>
                     <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                <LuBarChart3 className="text-gray-500" />
                Chart Type
              </label>
              <Select
                className="w-full"
                options={chartTypeOptions}
                label=""
                getOptionValue={(option) => option.value}
                getOptionLabel={(option) => option.label}
                onChange={(val) => handleSectionFilterChange("chartType", String(val) || "line")}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                <LuCalendar className="text-gray-500" />
                Time Granularity
              </label>
              <Select
                className="w-full"
                options={timeGranularityOptions}
                label=""
                getOptionValue={(option) => option.value}
                getOptionLabel={(option) => option.label}
                onChange={(val) => handleSectionFilterChange("timeGranularity", String(val) || "daily")}
              />
            </div>
            
                         <div>
               <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                 <LuUsers className="text-gray-500" />
                 Role Filter
               </label>
               <Select
                 className="w-full"
                 options={[
                   { value: "ALL", label: "All Roles" },
                   { value: "LEARNER", label: "Learners" },
                   { value: "COACH", label: "Coaches" },
                   { value: "DUAL", label: "Dual Role" },
                 ]}
                 label=""
                 getOptionValue={(option) => option.value}
                 getOptionLabel={(option) => option.label}
                 onChange={(val) => handleSectionFilterChange("role", String(val) || "ALL")}
               />
             </div>
             
                          <div>
               <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                 <LuCalendar className="text-gray-500" />
                 Language
               </label>
               <Select
                 className="w-full"
                 options={languageTuples}
                 label=""
                 includeOption="All Languages"
                 getOptionValue={(tuple) => tuple[0]}
                 getOptionLabel={(tuple) => tuple[1]}
                 onChange={(val) => handleSectionFilterChange("language", val as UserLangCode)}
               />
               <div className="mt-1 text-xs text-gray-500">
                 <p><strong>Dual Role:</strong> Shows users with the selected language in either coaching or learning preference</p>
                 <p><strong>Learner:</strong> Shows users with the selected language in learning preference</p>
                 <p><strong>Coach:</strong> Shows users with the selected language in coaching preference</p>
               </div>
             </div>
             
             <div>
               <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                 <LuCalendar className="text-gray-500" />
                 Timezone
               </label>
               <Select
                 className="w-full"
                 options={getTimezoneOptionsForUserGrowth()}
                 label=""
                 getOptionValue={(option) => option.value}
                 getOptionLabel={(option) => option.label}
                 onChange={(val) => handleSectionFilterChange("timeZone", String(val))}
               />
             </div>
            
                         {sectionFilters.timeGranularity === "custom" && (
               <>
                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                     <LuCalendar className="text-gray-500" />
                     Start Date
                   </label>
                   <input
                     type="date"
                     value={sectionFilters.startDate ? sectionFilters.startDate.toISOString().split('T')[0] : ""}
                     max={sectionFilters.endDate ? sectionFilters.endDate.toISOString().split('T')[0] : undefined}
                     onChange={(e) => {
                       const startDate = e.target.value ? new Date(e.target.value) : undefined;
                       setSectionFilters(prev => ({ 
                         ...prev, 
                         startDate,
                         // Reset end date if start date is after current end date
                         endDate: prev.endDate && startDate && startDate > prev.endDate ? undefined : prev.endDate
                       }));
                     }}
                     className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                   />
                 </div>
                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                     <LuCalendar className="text-gray-500" />
                     End Date
                   </label>
                   <input
                     type="date"
                     value={sectionFilters.endDate ? sectionFilters.endDate.toISOString().split('T')[0] : ""}
                     min={sectionFilters.startDate ? sectionFilters.startDate.toISOString().split('T')[0] : undefined}
                     max={new Date().toISOString().split('T')[0]}
                     onChange={(e) => {
                       const endDate = e.target.value ? new Date(e.target.value) : undefined;
                       setSectionFilters(prev => ({ 
                         ...prev, 
                         endDate
                       }));
                     }}
                     className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                   />
                 </div>
                 <div className="col-span-full">
                   <div className="text-xs text-gray-500 bg-yellow-50 p-2 rounded border border-yellow-200">
                     <p>⚠️ <strong>Note:</strong> Large date ranges may take longer to load. For best performance, keep ranges under 90 days.</p>
                   </div>
                 </div>
               </>
             )}
          </div>
          
                     <div className="mt-4 flex flex-wrap gap-2">
             <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
               Chart: {chartTypeOptions.find(c => c.value === sectionFilters.chartType)?.label}
             </span>
             <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
               Period: {timeGranularityOptions.find(t => t.value === sectionFilters.timeGranularity)?.label}
             </span>
             <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800">
               Role: {sectionFilters.role}
             </span>
             {sectionFilters.language && (
               <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                 Language: {languageTuples.find(t => t[0] === sectionFilters.language)?.[1] || sectionFilters.language}
               </span>
             )}
             {sectionFilters.timeZone && sectionFilters.timeZone !== "ALL" && (
               <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800">
                 Timezone: {sectionFilters.timeZone}
               </span>
             )}
           </div>
        </div>
      )}

      {/* Chart Container */}
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
        <div className="mb-4">
          <h4 className="text-lg font-semibold text-gray-800 mb-2">
            User Growth Trends
          </h4>
        </div>
        
        {/* Loading State */}
        {isInitialLoading && (
          <TrendChartLoading 
            chartType={sectionFilters.chartType as "line" | "area" | "bar"}
            timeGranularity={sectionFilters.timeGranularity}
            metric="users"
          />
        )}
        
        {/* Error State */}
        {error && !isInitialLoading && (
          <div className="h-96 flex items-center justify-center">
            <div className="text-center">
              <FaExclamationTriangle className="text-red-500 text-4xl mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Failed to Load User Growth Data</h3>
              <p className="text-gray-600 mb-4">{error}</p>
              <Button
                type="button"
                onClick={refetch}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 mx-auto"
              >
                <LuRefreshCw className="text-sm" />
                Retry
              </Button>
            </div>
          </div>
        )}
        
        {/* Chart or Loading State */}
        {!isInitialLoading && !error && (
          loading ? (
            <TrendChartLoading 
              chartType={sectionFilters.chartType as "line" | "area" | "bar"}
              timeGranularity={sectionFilters.timeGranularity}
              metric="users"
            />
          ) : (
            renderChart()
          )
        )}
      </div>

    </div>
  );
};
